import { pick } from "es-toolkit";
import { action, computed, makeObservable, observable } from "mobx";
import i18n from "@/modules/common/i18n";
import { formatList } from "@/modules/common/i18n/listFormat";
import { IsInvalid, IsValid, type ValidationResult } from "../../components/ValidatingSubmitButton";
import type { GalleryFile } from "../../eln/gallery/useGalleryListing";
import { UnparsableString } from "../../util/error";
import type { URL as URLType } from "../../util/types";
import type { Attachment } from "../definitions/Attachment";
import type { GlobalId, Id } from "../definitions/BaseRecord";
import type { Field, FieldLink, FieldType, Option, OptionValue } from "../definitions/Field";
import type { Sample } from "../definitions/Sample";
import { type AttachmentJson, newAttachment, newExistingAttachment, newGalleryAttachment } from "./AttachmentModel";
import { apiStringToFieldType, type FieldType as FieldTypeSymbol } from "./FieldTypes";
import type InventoryBaseRecord from "./InventoryBaseRecord";

const formatOption = (option: OptionValue): Option => ({
  value: option,
  label: option,
  editing: false,
});

export function hasOptions(type: FieldType): boolean {
  return type === "radio" || type === "choice";
}

export type FieldModelAttrs = {
  id?: number;
  globalId?: GlobalId;
  name?: string;
  type: FieldType;
  content?: string | null;
  selectedOptions: Array<OptionValue> | null;
  definition?: {
    options: Array<OptionValue>;
  } | null;
  initial?: boolean;
  editing?: boolean;
  columnIndex: number | null;
  attachment: AttachmentJson | null;
  mandatory: boolean;
  allowedRelationTypes?: Array<string> | null;
  link?: FieldLink | null;
};

export default class FieldModel implements Field {
  // @ts-expect-error Initialised by setAttributes
  id: Id;
  // @ts-expect-error Initialised by setAttributes
  globalId: GlobalId | null;
  // @ts-expect-error Initialised by setAttributes
  type: FieldType;
  // @ts-expect-error Initialised by setAttributes
  name: string;
  // @ts-expect-error Initialised by setAttributes
  content: string | number | Date;
  // @ts-expect-error Initialised by setAttributes
  selectedOptions: Array<string> | null;
  definition: object = {};
  initial: boolean = false;
  editing: boolean = false;
  deleteFieldRequest: boolean = false;
  deleteFieldOnSampleUpdate: boolean = false;
  options: Array<Option> = [];
  // @ts-expect-error Initialised by setAttributes
  owner: Sample;
  // @ts-expect-error Initialised by setAttributes
  columnIndex: number;
  // @ts-expect-error Initialised by setAttributes
  attachment: Attachment | null;
  originalAttachment: Attachment | null = null;
  // @ts-expect-error Initialised by setAttributes
  mandatory: boolean;
  error: boolean;
  linkEditInProgress: boolean = false;
  /** Link template fields: permitted DataCite relation types; empty = all allowed. */
  allowedRelationTypes: Array<string> = [];
  /** Link fields: the single link value, or null when unset. */
  link: FieldLink | null = null;

  constructor(_params: FieldModelAttrs, owner: InventoryBaseRecord) {
    makeObservable(this, {
      id: observable,
      globalId: observable,
      type: observable,
      name: observable,
      content: observable,
      definition: observable,
      initial: observable,
      editing: observable,
      deleteFieldRequest: observable,
      deleteFieldOnSampleUpdate: observable,
      options: observable,
      owner: observable,
      columnIndex: observable,
      selectedOptions: observable,
      attachment: observable,
      mandatory: observable,
      error: observable,
      linkEditInProgress: observable,
      allowedRelationTypes: observable,
      link: observable,
      setAttributesDirty: action,
      setAttributes: action,
      setEditing: action,
      setError: action,
      setLinkEditInProgress: action,
      fieldType: computed,
      optionsAreUnique: computed,
      hasContent: computed,
    });

    let params = pick(_params, [
      "id",
      "globalId",
      "attachment",
      "content",
      "selectedOptions",
      "type",
      "name",
      "initial",
      "mandatory",
      "allowedRelationTypes",
      "link",
    ]) as unknown as object & {
      content: string | number | Date;
      type: FieldType;
      initial: boolean;
      attachment?: AttachmentJson;
      owner: InventoryBaseRecord;
      options: Array<OptionValue> | Array<Option>;
      allowedRelationTypes?: Array<string> | null;
      link?: FieldLink | null;
    };

    params.initial = params.initial ?? false;
    params.allowedRelationTypes = params.allowedRelationTypes ?? [];
    params.link = params.link ?? null;

    if (_params.definition) {
      params = {
        ...params,
        ..._params.definition,
      };
    }

    params.content = params.content ?? "";

    if (params.type === "time") {
      if (params.content === "") {
        // do nothing; empty string is valid value when not set
      } else if (typeof params.content === "string" && params.content.length === 5) {
        // as "hh:mm"
        const date = new Date();
        const [h, m] = params.content.split(":").map((n) => Number(n));
        date.setHours(h);
        date.setMinutes(m);
        params.content = date;
        /*
         * Note that whilst this does result in the content being set to a full
         * date object, we only use the time component throughout the UI.
         */
      } else {
        throw new UnparsableString(params.content as string, "Invalid time format");
      }
    }

    if (hasOptions(params.type)) {
      params.options = (params.options as Array<OptionValue>).map(formatOption);
    }

    if (params.type === "number") {
      if (Number.isNaN(params.content as number)) params.content = "";
      if (params.content !== "") {
        params.content = parseFloat(params.content as string);
      }
    }

    params.owner = owner;
    const attachment = params.attachment;
    delete params.attachment;
    this.setAttributes(params);

    // have to do this setAttributes after because this.permalinkURL needs to be set
    if (attachment)
      this.setAttributes({
        attachment: newExistingAttachment(
          attachment,
          // @ts-expect-error owner is set by the setAttributes call above
          this.owner.id ? this.permalinkURL : "",
          (a: Attachment) => {
            if (!this.originalAttachment) this.originalAttachment = a;
            this.setAttributesDirty({});
          },
        ),
      });
    this.error = false;
  }

  // biome-ignore lint/complexity/noBannedTypes: initial biome migration
  setAttributesDirty(attrs: {}) {
    this.owner.setAttributesDirty({});
    this.setAttributes(attrs);
  }

  // biome-ignore lint/complexity/noBannedTypes: initial biome migration
  setAttributes(attrs: {}) {
    Object.assign(this, attrs);
  }

  setEditing(editing: boolean) {
    this.editing = editing;
  }

  setError(error: boolean) {
    this.error = error;
  }

  setLinkEditInProgress(value: boolean) {
    this.linkEditInProgress = value;
  }

  get fieldType(): FieldTypeSymbol {
    return apiStringToFieldType(this.type);
  }

  get optionsAreUnique(): boolean {
    const optionValues = this.options.map((o) => o.value);
    return new Set(optionValues).size === optionValues.length;
  }

  validate(): ValidationResult {
    if (this.name === "") return IsInvalid("Names for custom fields cannot be empty.");
    if (this.name.length > 50) return IsInvalid("Names for custom fields cannot exceed 50 characters.");
    if (this.type === "radio") {
      if (this.options.length === 0) return IsInvalid("Custom radio fields must have at least one option.");
      if (this.options.some((o) => o.value === "")) return IsInvalid("Options of custom radio fields cannot be empty.");
      if (!this.optionsAreUnique) return IsInvalid("Options of custom radio fields must all be distinct.");
    }
    if (this.type === "choice") {
      if (this.options.length === 0) return IsInvalid("Custom choice fields must have at least one option.");
      if (this.options.some((o) => o.value === ""))
        return IsInvalid("Options of custom choice fields cannot be empty.");
      if (!this.optionsAreUnique) return IsInvalid("Options of custom choice fields must all be distinct.");
    }
    if (this.mandatory && this.owner.enforceMandatoryFields && !this.hasContent)
      return IsInvalid(`The mandatory custom field "${this.name}" must have a valid value.`);
    if (this.type === "link" && this.linkEditInProgress)
      return IsInvalid(`Apply or discard the link changes in "${this.name}" before saving.`);
    if (this.error) return IsInvalid(`There is an error with the custom field, "${this.name}".`);
    return IsValid();
  }

  get hasContent(): boolean {
    if (hasOptions(this.type)) return Boolean(this.selectedOptions?.length);
    if (this.type === "number") return this.content !== "";
    if (this.type === "link") return Boolean(this.link?.targetGlobalId);
    return Boolean(this.content);
  }

  get renderContentAsString(): string {
    if (hasOptions(this.type)) return formatList(this.selectedOptions ?? [], i18n.resolvedLanguage ?? i18n.language);
    return this.content.toString() ?? "";
  }

  setAttachment(file: File | GalleryFile): void {
    if (this.attachment) this.attachment.remove();

    this.setAttributesDirty({
      attachment:
        file instanceof File
          ? newAttachment(file, this.owner.id ? this.permalinkURL : "", (attachment) => {
              if (!this.originalAttachment) this.originalAttachment = attachment;
              this.setAttributesDirty({});
            })
          : newGalleryAttachment(file, (attachment) => {
              if (!this.originalAttachment) this.originalAttachment = attachment;
              this.setAttributesDirty({});
            }),
    });
  }

  get permalinkURL(): URLType | null {
    return this.owner.permalinkURL;
  }

  get paramsForBackend(): object {
    const ret: typeof this & { newFieldRequest?: boolean } = { ...this };
    if (!ret.id) ret.newFieldRequest = true;

    switch (ret.type) {
      case "radio":
      case "choice":
        if (typeof this.name === "string")
          ret.definition = {
            options: this.options.map((o) => o.value),
          };
        break;
      case "time":
        if (this.content instanceof Date)
          ret.content = this.content.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
        ret.content = ret.content ?? "";
        break;
      case "date":
        ret.content = ret.content ?? "";
        break;
    }
    const keys = [
      "id",
      "name",
      "type",
      "definition",
      "newFieldRequest",
      "deleteFieldRequest",
      "deleteFieldOnSampleUpdate",
      "columnIndex",
      "mandatory",
    ];
    if (ret.type === "link") {
      // link fields carry their permitted relation-type whitelist and the
      // chosen link value; their data column is unused, so no content is sent
      // (the backend's mandatory check rejects empty content)
      keys.push("allowedRelationTypes", "link");
    } else {
      keys.push(hasOptions(ret.type) ? "selectedOptions" : "content");
    }
    return pick(ret, keys as Array<keyof typeof ret>) as object;
  }
}
